import { ReactComponent as ExternalLinkSvg } from '@frontend/icons/external-link-2.svg';
import {
  Button,
  Icon,
  PhoneMockup,
  PhoneMockupBodyLayout,
  PhoneMockupLayout,
  WidgetStatus,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';

import { useEffect } from 'react';
import {
  BackButton,
  PaymentPageWidgetHeader,
  TopBar,
  TransactionDropdown,
  TransactionDropdownProps,
} from '../../components';
import links from '../../constants/links';
import { useWidgetContext } from '../../context/WidgetContext';
import { useRegisterHotjarPage } from '../../hooks';
import { isOfframpBill } from '../../utils';

export interface PaymentWaitingProps {
  merchantName: string;
  billExpiresAt?: number;
  transactions?: TransactionDropdownProps['transactions'];
  paymentExpiresAt?: number;
  paymentStartedAt?: number;
  exchangeRate?: number;
  fiatCurrency?: string;
  cryptoCurrency?: string;
  tickerImageSrc?: string;
  billType: string;
  onGoBack: () => void;
}

const PaymentWaiting = ({
  billExpiresAt,
  paymentExpiresAt,
  paymentStartedAt,
  exchangeRate,
  fiatCurrency,
  cryptoCurrency,
  tickerImageSrc,
  merchantName,
  transactions,
  billType,
  onGoBack,
}: PaymentWaitingProps) => {
  const { configureHeader } = useWidgetContext();
  useRegisterHotjarPage('payment-waiting');

  const isOffRamp = isOfframpBill(billType);

  useEffect(() => {
    configureHeader({
      id: getIdFromUrl(),
      expiresAt: billExpiresAt,
      merchantName,
      onMobileBackButtonClick: () => onGoBack,
    });
  }, []);

  return (
    <PhoneMockupLayout backButton={<BackButton onClick={onGoBack} />}>
      <PhoneMockup
        header={
          <PaymentPageWidgetHeader
            paymentExpiresAt={paymentExpiresAt}
            paymentStartedAt={paymentStartedAt}
            exchangeRate={exchangeRate}
            fiatCurrency={fiatCurrency}
            cryptoCurrency={cryptoCurrency}
            tickerImageSrc={tickerImageSrc}
            billType={billType}
          />
        }
      >
        <PhoneMockupBodyLayout
          bodyCentered
          header={
            !!transactions && (
              <TopBar
                rightSlot={<TransactionDropdown transactions={transactions} />}
              />
            )
          }
          body={
            <WidgetStatus
              emoji="🔎"
              title={`Looking for your ${
                isOffRamp ? 'transaction' : 'payment'
              }`}
              description={`We need to confirm your ${
                isOffRamp ? 'transaction' : 'payment'
              } on the blockchain for it to become available. This can take a few moments.`}
            />
          }
          footer={
            <>
              <Button
                type="button"
                onClick={onGoBack}
                fullWidth
                data-test-id="notPaid"
              >
                Back
              </Button>
              <Button
                mt="8"
                as="a"
                size="large"
                href={links.support.PAYMENT_CONFIRMATION}
                target="_blank"
                rel="noopener noreferrer"
                variant="ghost"
                rightIcon={<Icon component={<ExternalLinkSvg />} />}
              >
                Learn more
              </Button>
            </>
          }
        />
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export default PaymentWaiting;
